(() => {
  if (window.BioselfMobileNavigation) return;

  const FOCUSABLE = 'a[href], button:not([disabled]), summary, select, [tabindex]:not([tabindex="-1"])';

  class MobileNavigation {
    constructor(root) {
      this.root = root;
      this.panels = [...root.querySelectorAll('[data-mobile-panel]')];
      this.scrim = root.querySelector('[data-mobile-panel-close]');
      this.activePanel = null;
      this.lastActiveElement = null;
      this.activeSubmenuTrigger = null;
      this.stickyCart = document.querySelector('[data-mobile-sticky-cart]');
      this.dock = root.querySelector('#footer_nav_mobile');
      this.currentPageItems = [...root.querySelectorAll('#footer_nav_mobile [aria-current="page"]')];
      this.lastScrollY = Math.max(window.scrollY, 0);
      this.scrollFrame = null;
      this.standardAddToCart = document.querySelector(
        '#product_page_custom_mobile #add-to-cart, #product_page_custom_mobile a.notify_button_collection'
      );
      this.customFooter = document.getElementById('custom_footer_mobile');

      document.body.classList.add('bioself-has-mobile-nav');
      root.addEventListener('click', (event) => this.onRootClick(event));
      document.addEventListener('click', (event) => this.onDocumentClick(event));
      document.addEventListener('keydown', (event) => this.onKeyDown(event));
      document.addEventListener('change', (event) => this.onChange(event));
      window.addEventListener('resize', () => this.onViewportChange());
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => this.updateStickyCart());
        window.visualViewport.addEventListener('scroll', () => this.updateStickyCart());
      }

      this.cartStateObserver = new MutationObserver(() => {
        const cartIsOpen = document.body.classList.contains('bioself-cart-drawer-open');
        const mobilePanelIsOpen =
          Boolean(this.activePanel) ||
          document.body.classList.contains('bioself-mobile-panel-open') ||
          this.panels.some(
            (panel) =>
              panel.classList.contains('is-open') || panel.getAttribute('aria-hidden') === 'false'
          );

        if (cartIsOpen && mobilePanelIsOpen) this.close(false);
        this.updateStickyCart();
      });
      this.cartStateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      this.prepareStickyCart();
    }

    onRootClick(event) {
      const opener = event.target.closest('[data-mobile-panel-open]');
      if (opener) {
        event.preventDefault();
        const panelId = opener.getAttribute('data-mobile-panel-open');
        const panel = this.panels.find((candidate) => candidate.id === panelId);
        const isExpanded =
          opener.getAttribute('aria-expanded') === 'true' ||
          panel?.classList.contains('is-open') ||
          panel?.getAttribute('aria-hidden') === 'false' ||
          this.activePanel?.id === panelId;

        if (isExpanded) this.close();
        else this.open(panelId, opener);
        return;
      }

      const submenuOpener = event.target.closest('[data-mobile-menu-view-open]');
      if (submenuOpener) {
        event.preventDefault();
        this.openSubmenu(submenuOpener.getAttribute('data-mobile-menu-view-open'), submenuOpener);
        return;
      }

      if (event.target.closest('[data-mobile-menu-view-back]')) {
        event.preventDefault();
        this.closeSubmenu();
        return;
      }

      if (event.target.closest('[data-mobile-panel-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-mobile-panel] a[href]')) this.close(false);
    }

    onDocumentClick(event) {
      const blogCategoriesTrigger = event.target.closest('.page-see_cat_btn');
      if (!blogCategoriesTrigger) return;
      event.preventDefault();
      this.open('blog_custom_cat_mobile_modal', blogCategoriesTrigger);
    }

    onChange(event) {
      const languageSelect = event.target.closest('[data-localization-select]');
      if (!languageSelect) return;
      languageSelect.form?.submit();
    }

    open(panelId, trigger) {
      const panel = this.panels.find((candidate) => candidate.id === panelId);
      if (!panel) return;

      this.close(false);
      this.lastActiveElement = trigger || document.activeElement;
      this.activePanel = panel;
      this.resetPanelViews(panel);
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('bioself-mobile-panel-open');
      this.setCurrentPageState(false);
      this.setTriggerState(panelId, true);
      panel.focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      const focusTarget = this.lastActiveElement;

      this.panels.forEach((panel) => {
        if (panel.classList.contains('is-open')) panel.classList.remove('is-open');
        if (panel.getAttribute('aria-hidden') !== 'true') panel.setAttribute('aria-hidden', 'true');
        this.resetPanelViews(panel);
      });
      this.activePanel = null;
      if (document.body.classList.contains('bioself-mobile-panel-open')) {
        document.body.classList.remove('bioself-mobile-panel-open');
      }
      this.root
        .querySelectorAll('[data-mobile-panel-open]')
        .forEach((trigger) => {
          if (trigger.getAttribute('aria-expanded') !== 'false') {
            trigger.setAttribute('aria-expanded', 'false');
          }
        });
      this.setCurrentPageState(true);

      if (restoreFocus) focusTarget?.focus?.({ preventScroll: true });
      this.lastActiveElement = null;
    }

    openSubmenu(viewId, trigger) {
      if (!this.activePanel || !viewId) return;

      const views = [...this.activePanel.querySelectorAll('[data-mobile-panel-view]')];
      const targetView = views.find((view) => view.getAttribute('data-mobile-panel-view') === viewId);
      if (!targetView) return;

      views.forEach((view) => {
        view.hidden = view !== targetView;
      });
      this.activePanel
        .querySelectorAll('[data-mobile-menu-view-open]')
        .forEach((button) => button.setAttribute('aria-expanded', String(button === trigger)));
      this.activeSubmenuTrigger = trigger;

      const focusTarget = targetView.querySelector('[data-mobile-menu-view-back]') || targetView;
      focusTarget.focus({ preventScroll: true });
    }

    closeSubmenu(restoreFocus = true) {
      if (!this.activePanel) return;

      const focusTarget = this.activeSubmenuTrigger;
      this.resetPanelViews(this.activePanel);
      if (restoreFocus) focusTarget?.focus?.({ preventScroll: true });
    }

    resetPanelViews(panel) {
      panel.querySelectorAll('[data-mobile-panel-view]').forEach((view) => {
        view.hidden = view.getAttribute('data-mobile-panel-view') !== 'root';
      });
      panel
        .querySelectorAll('[data-mobile-menu-view-open]')
        .forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.activeSubmenuTrigger = null;
    }

    setTriggerState(panelId, expanded) {
      this.root
        .querySelectorAll(`[data-mobile-panel-open="${panelId}"]`)
        .forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
    }

    setCurrentPageState(isCurrent) {
      this.currentPageItems.forEach((item) => {
        if (isCurrent) item.setAttribute('aria-current', 'page');
        else item.removeAttribute('aria-current');
      });
    }

    onKeyDown(event) {
      if (!this.activePanel) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...this.activePanel.querySelectorAll(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onViewportChange() {
      if (window.innerWidth > 1000) {
        this.close(false);
        this.dock?.classList.remove('is-hidden-by-scroll');
      }
      this.lastScrollY = Math.max(window.scrollY, 0);
      this.updateStickyCart();
    }

    onScroll() {
      this.updateStickyCart();

      if (this.scrollFrame !== null) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.updateDockVisibility();
        this.scrollFrame = null;
      });
    }

    updateDockVisibility() {
      if (!this.dock || window.innerWidth > 1000) return;

      const currentScrollY = Math.max(window.scrollY, 0);
      const scrollDelta = currentScrollY - this.lastScrollY;
      const isInteractionOpen =
        document.body.classList.contains('bioself-cart-drawer-open') ||
        document.body.classList.contains('bioself-mobile-panel-open');

      if (currentScrollY <= 24) {
        this.dock.classList.remove('is-hidden-by-scroll');
        this.lastScrollY = currentScrollY;
        return;
      }

      if (Math.abs(scrollDelta) <= 6) return;

      if (!isInteractionOpen && scrollDelta > 0) {
        this.dock.classList.add('is-hidden-by-scroll');
      } else if (scrollDelta < 0) {
        this.dock.classList.remove('is-hidden-by-scroll');
      }

      this.lastScrollY = currentScrollY;
    }

    prepareStickyCart() {
      if (!this.stickyCart || !this.standardAddToCart) return;
      this.stickyCart.hidden = false;
      this.updateStickyCart();
    }

    updateStickyCart() {
      if (!this.stickyCart || !this.standardAddToCart) return;

      const isMobile = window.innerWidth <= 1000;
      const primaryButtonIsAboveViewport = this.standardAddToCart.getBoundingClientRect().bottom < 0;
      const footerIsVisible = this.customFooter
        ? this.customFooter.getBoundingClientRect().top < (window.visualViewport?.height || window.innerHeight)
        : false;
      const shouldShow = isMobile && primaryButtonIsAboveViewport && !footerIsVisible;

      this.stickyCart.classList.toggle('is-visible', shouldShow);
    }
  }

  const roots = [...document.querySelectorAll('[data-mobile-navigation]')];
  window.BioselfMobileNavigation = roots.map((root) => new MobileNavigation(root));
})();
